import { Label, RadioCardGroup, CopyField, TooltipProvider } from '@teach/ui'
import { useLanguage } from '@/hooks/useLanguage'
import { useGetAvailableCategoriesQuery } from '@/features/categories/categoriesApi'
import type { AccessType } from '../../../types'
import type { ArticleAccess } from '../useArticleAccess'
import { ArticleAccessControl } from './ArticleAccessControl'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const PUBLIC_SITE_URL = import.meta.env.VITE_PUBLIC_SITE_URL
if (!PUBLIC_SITE_URL && import.meta.env.PROD) {
  throw new Error('VITE_PUBLIC_SITE_URL is required in production')
}

interface Props {
  /** Показанный доступ: из модерации, до её ответа — живое поле статьи. */
  accessType: AccessType
  articleAccess: ArticleAccess
  /** Несохранённая форма: на проверку ушла бы не та версия, что видит автор. */
  dirty: boolean
  /** Статья на проверке (`useIsArticleFrozen` владельца): селект и поля заморожены. */
  frozen: boolean
  isCreate: boolean
  slug?: string | null
  activeLang: 'ru' | 'en'
  categoryId: number | null
  onCategoryIdChange: (id: number | null) => void
}

/** Почему селект погашен — строкой под ним. Проверку и отсчёт `429` объясняют свои места. */
const lockHint = (m: (typeof messages)['ru'], isCreate: boolean, dirty: boolean): string | null => {
  if (isCreate) return m.saveToPublish
  return dirty ? m.saveChangesFirst : null
}

export const AccessTab = ({
  accessType,
  articleAccess,
  dirty,
  frozen,
  isCreate,
  slug,
  activeLang,
  categoryId,
  onCategoryIdChange,
}: Props) => {
  const { language } = useLanguage()
  const m = useMessages(messages)
  const { data: categories = [] } = useGetAvailableCategoriesQuery({ kind: 'instruction' })

  const baseUrl = PUBLIC_SITE_URL ?? window.location.origin
  const publicUrl = slug ? `${baseUrl}/guides/${activeLang}/guide/${slug}` : ''

  // `CopyField` держится на Radix `Tooltip`, а глобального провайдера в приложении нет.
  return (
    <TooltipProvider>
      <div className="space-y-6">
        <ArticleAccessControl
          accessType={accessType}
          articleAccess={articleAccess}
          hint={lockHint(m, isCreate, dirty)}
          frozen={frozen}
        />

        {accessType === 'public' ? (
          <div className="space-y-2">
            <Label className="text-sm font-medium">{m.catalogSection}</Label>
            <RadioCardGroup
              options={categories.map((c) => ({
                id: String(c.id),
                label: language === 'en' ? c.nameEn : c.nameRu,
              }))}
              value={categoryId != null ? String(categoryId) : null}
              onChange={(id) => onCategoryIdChange(Number(id))}
              // Поле формы: под проверкой заморожено, как и остальные.
              disabled={frozen}
            />
          </div>
        ) : null}

        {accessType !== 'private' && publicUrl ? (
          <div className="space-y-2">
            <Label className="text-sm font-medium">{m.articleLink}</Label>
            <CopyField value={publicUrl} toastText={m.linkCopied} />
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  )
}
